import { Box, Icon, Typography } from '@mui/material'
import { black, grey, shade } from '../../../colors'
import React, { useEffect } from 'react'
import theme from '../../../theme'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { type IUser } from '../../../models/User'
import MultiSelectWithCheckBox from '../../../components/FormElements/MultiSelectWithCheckBox'
import { useFormContext } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import UserManager from '../../../APIManager/UserManager'

interface StudentEnrollmentListItemProps {
  index: number
  enrolled: boolean
  handlePressed: () => void
  readOnly?: boolean
  student: IUser
  sectionId?: string
}

export const StudentEnrollmentListItem: React.FC<StudentEnrollmentListItemProps> = ({
  index,
  enrolled,
  handlePressed,
  readOnly,
  student,
  sectionId
}) => {
  const backgroundColor = index % 2 === 0 ? 'none' : shade[1]
  const studentName = student.name
  const studentEmail = student?.email
  const { data: instructorsRaw = [], isLoading: instructorsLoading } = useQuery({
    queryKey: ['instructors', sectionId],
    queryFn: async () => {
      if (!sectionId) return []
      const res = await UserManager.getInstructorsBySectionIdApi(sectionId)
      return res.data || []
    },
    enabled: !!sectionId
  })
  const uniqueInstructors = React.useMemo(() => {
    const instructorsList: IUser[] = []
    const userIdSet = new Set<string>()
    instructorsRaw.forEach((section: any) => {
      if (Array.isArray(section.instructors)) {
        section.instructors.forEach((instructor: IUser) => {
          if (instructor.userId && !userIdSet.has(instructor.userId)) {
            userIdSet.add(instructor.userId)
            instructorsList.push(instructor)
          }
        })
      }
    })
    return instructorsList
  }, [instructorsRaw])

  const { setValue } = useFormContext()

  const studentEnrollment = React.useMemo(() => {
    if (Array.isArray(instructorsRaw) && instructorsRaw.length > 0) {
      return instructorsRaw.find((section: any) => section.user?.userId === student.userId) || null
    }
    return null
  }, [instructorsRaw, student.userId])

  useEffect(() => {
    if (studentEnrollment) {
      let selectedInstructors: IUser[] = []
      if (studentEnrollment.isEnrollmentForAllInstructors) {
        selectedInstructors = studentEnrollment.instructors || []
      } else {
        selectedInstructors = (studentEnrollment.instructors || []).filter((inst: any) => inst.isInstructorSelected)
      }
      setValue(`instructors-${student.userId}`, selectedInstructors)
    }
  }, [studentEnrollment, setValue, student.userId])
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor,
        px: theme.spacing(4),
        borderRadius: theme.spacing(1),
        gap: theme.spacing(4),
        width: '100%'
        // flexWrap: 'nowrap'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: theme.spacing(2) }} width={'40%'}>
        <Typography
          variant="h6"
          fontWeight={700}
          fontSize={16}
          color={black[100]}
          lineHeight={1.4}
        >
          {studentName}
        </Typography>
        <Typography variant="body2" color="grey.500" fontSize={14}>
          {studentEmail}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing(1.5),
          mt: 5,
          width: '50%',
          maxWidth: '50%',
          py: theme.spacing(1)
        }}
      >
        <MultiSelectWithCheckBox
          options={uniqueInstructors || []}
          loading={instructorsLoading}
          label=""
          name={`instructors-${student.userId}`}
          placeholder='No Instructor Selected'
          getOptionLabel={(option: IUser) => option?.name}
          getOptionSubLabel={(option: IUser) => {
            if (!option?.email) return ''
            const [local, domain] = option.email.split('@')
            return `${local} @${domain}`
          }}
          getOptionValue={(option: IUser) => option?.userId}
          showSelectAll
          clearIcon={false}
          fullWidth
          getOptionDisabled={(option: IUser) => !option.isActive}
          sx={{
            '& .MuiInputBase-root': {
              width: '210px',
              minWidth: '210px',
              maxWidth: '210px'
            }
          }}
          renderTags={(value) => (
            <Box
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '150px',
                minWidth: '150px',
                maxWidth: '150px',
                px: theme.spacing(1),
                color: grey[400],
                fontWeight: 300
              }}
            >
              {studentEnrollment?.isEnrollmentForAllInstructors
                ? 'All Instructors'
                : value.length === 0
                  ? 'No Instructor Selected'
                  : value.map((option) => option.name).join(', ')
              }
            </Box>
          )}
        />

        {!readOnly && (
          <Box width={'8%'}>
            <Icon
              component={enrolled ? CloseIcon : AddIcon}
              color="action"
              onClick={handlePressed}
              sx={{
                cursor: 'pointer',
                fontSize: 24,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.6 },
                alignSelf: 'center',
                mb: 6
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}
